import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CssBaseline, Typography, CircularProgress } from "@mui/material";

import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import { routes } from "../../constants";
import { useQuickTalesContext } from "../../providers/OrganizationAdmin/QuickTalesProvider";
import { useReactionsContext } from "../../providers/OrganizationAdmin/ReactionsProvider";

const linkStyle = {
  cursor: "pointer",
  fontSize: "18px",
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 500,
  marginBottom: "12px",
  "&:hover": {
    textDecoration: "underline",
  },
};

const Home = () => {
  const navigate = useNavigate();
  const { handleGetAllQuickTales, pendingQuickTales } = useQuickTalesContext();
  const { handleGetAllReactions, allReactions } = useReactionsContext();
  const [countsLoading, setCountsLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setCountsLoading(true);
        await Promise.all([handleGetAllQuickTales(), handleGetAllReactions()]);
      } catch (error) {
        console.error("Error fetching home page counts:", error);
      } finally {
        setCountsLoading(false);
      }
    };
    fetchCounts();
  }, []);

  const pendingReactionsCount = allReactions?.filter(
    (r) => r.status?.toLowerCase() === "pending"
  ).length ?? 0;

  const pendingQTCount = pendingQuickTales?.length ?? 0;

  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <Header mainTitle={"Home"} bgColor="#fff" />
      <Sidebar />
      <Box
        sx={{
          background: "#fff",
          width: "100%",
          marginTop: 6,
          padding: 4,
          fontFamily: "Avenir Book, sans-serif",
          marginLeft: 12
        }}
      >
        {/* ===== Section 1: Get Started ===== */}
        <Box sx={{ marginBottom: 5 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontFamily: "Avenir Book, sans-serif",
              marginBottom: 1,
            }}
          >
            Get Started
          </Typography>
          <Typography
            sx={{
              fontStyle: "italic",
              fontSize: "16px",
              fontFamily: "Avenir Book, sans-serif",
              color: "#555",
              marginBottom: 2,
            }}
          >
            Create the core content for your visitor experience.
          </Typography>
          <Box sx={{ paddingLeft: 3 }}>
            <Typography
              onClick={() => navigate(routes.createQuickTale)}
                sx={{
                  fontSize: "18px",
                  fontFamily: "Avenir Book, sans-serif",
                  color: "#00a7cd",
                  mb: 1,
                  '&:hover': {
                    color: "#9447AF", // darker shade on hover
                  },
                }}
            >
              Create a QuickTale →
            </Typography>
            <Typography
              onClick={() => navigate(routes.addArea)}
                sx={{
                  fontSize: "18px",
                  fontFamily: "Avenir Book, sans-serif",
                  color: "#00a7cd",
                  mb: 1,
                  '&:hover': {
                    color: "#9447AF", // darker shade on hover
                  },
                }}
            >
              Create an Area →
            </Typography>
          </Box>
        </Box>

        {/* ===== Section 2: Invite Others to Contribute ===== */}
        <Box sx={{ marginBottom: 5 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontFamily: "Avenir Book, sans-serif",
              marginBottom: 1,
            }}
          >
            Invite Others to Contribute
          </Typography>
          <Typography
            sx={{
              fontStyle: "italic",
              fontSize: "16px",
              fontFamily: "Avenir Book, sans-serif",
              color: "#555",
              marginBottom: 2,
            }}
          >
            Request stories from artists, experts, staff, students, or community members.
          </Typography>
          <Box sx={{ paddingLeft: 3 }}>
            <Typography
              onClick={() =>
                navigate(routes.batches, { state: { openImportModal: true } })
              }
                sx={{
                  fontSize: "18px",
                  fontFamily: "Avenir Book, sans-serif",
                  color: "#00a7cd",
                  mb: 1,
                  '&:hover': {
                    color: "#9447AF", // darker shade on hover
                  },
                }}
            >
              Import QuickTales →
            </Typography>
            <Typography
              onClick={() => navigate(routes.emailTemplates)}
                sx={{
                  fontSize: "18px",
                  fontFamily: "Avenir Book, sans-serif",
                  color: "#00a7cd",
                  mb: 1,
                  '&:hover': {
                    color: "#9447AF", // darker shade on hover
                  },
                }}
            >
              Select Email Template →
            </Typography>
            <Typography
              onClick={() => navigate(routes.batches)}
                sx={{
                  fontSize: "18px",
                  fontFamily: "Avenir Book, sans-serif",
                  color: "#00a7cd",
                  mb: 1,
                  '&:hover': {
                    color: "#9447AF", // darker shade on hover
                  },
                }}
            >
              Send Request Emails to Contributors →
            </Typography>
          </Box>
        </Box>

        {/* ===== Section 3: Needs Your Attention ===== */}
        <Box sx={{ marginBottom: 5 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontFamily: "Avenir Book, sans-serif",
              marginBottom: 1,
            }}
          >
            Needs Your Attention
          </Typography>
          <Typography
            sx={{
              fontStyle: "italic",
              fontSize: "16px",
              fontFamily: "Avenir Book, sans-serif",
              color: "#555",
              marginBottom: 2,
            }}
          >
            Review new submissions and visitor responses.
          </Typography>
          <Box sx={{ paddingLeft: 3 }}>
            <Typography
              onClick={() => navigate(routes.addQuicktalesToOrg)}
                sx={{
                  fontSize: "18px",
                  fontFamily: "Avenir Book, sans-serif",
                  color: "#00a7cd",
                  mb: 1,
                  '&:hover': {
                    color: "#9447AF", // darker shade on hover
                  },
                }}
            >
              {countsLoading ? (
                <>
                  Review Submitted QuickTales{" "}
                  <CircularProgress size={14} sx={{ marginLeft: 1 }} /> →
                </>
              ) : (
                `Review Submitted QuickTales (${pendingQTCount}) →`
              )}
            </Typography>
            <Typography
              onClick={() => navigate(routes.reactionsInOrg)}
                sx={{
                  fontSize: "18px",
                  fontFamily: "Avenir Book, sans-serif",
                  color: "#00a7cd",
                  '&:hover': {
                    color: "#9447AF", // darker shade on hover
                  },
                }}
            >
              {countsLoading ? (
                <>
                  Review Audio Reactions{" "}
                  <CircularProgress size={14} sx={{ marginLeft: 1 }} />→
                </>
              ) : (
                `Review Audio Reactions (${pendingReactionsCount}) →`
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
